import React, { useEffect, useState, useRef, useCallback } from 'react';
import { injectIntl } from 'react-intl-next';
import { cssMap, styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N20, N100, G50 } from '@atlaskit/theme/colors';
import { FULL_PAGE_EDITOR_TOOLBAR_HEIGHT } from '@atlaskit/editor-shared-styles';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { usePubSubEvent } from '@confluence/pubsub-client';
import { MAXIMUM_TWO_LINE_TOOLBAR_BREAKPOINT } from '@confluence/editor-common-styles';

import type {
	EditorConnectionBannerType,
	EditorConnectionBannerAnalyticsType,
} from './EditorConnectionBannerTypes';
import { i18n } from './messages';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const StatusIndicatorOffline = styled.div<{
	show: boolean;
}>({
	display: 'grid',
	position: 'absolute',
	width: '100%',
	zIndex: 400,
	justifyContent: 'center',
	alignItems: 'center',
	textAlign: 'center',
	// We are holding on converting FULL_PAGE_EDITOR_TOOLBAR_HEIGHT until active content topper development is complete
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: FULL_PAGE_EDITOR_TOOLBAR_HEIGHT(),
	left: token('space.0', '0px'),
	paddingTop: token('space.075', '6px'),
	paddingBottom: token('space.075', '6px'),
	backgroundColor: token('color.background.accent.gray.subtlest', N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: `scaleY(${(props) => (props.show ? 1 : 0)})`,
	transformOrigin: 'top',
	transition: 'all 300ms ease',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media (max-width: ${MAXIMUM_TWO_LINE_TOOLBAR_BREAKPOINT}px)`]: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		top: FULL_PAGE_EDITOR_TOOLBAR_HEIGHT(),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const StyledText = styled.span({
	fontFamily: 'inherit',
	fontWeight: token('font.weight.bold'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	color: token('color.text.subtlest', N100),
	whiteSpace: 'normal',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const FadingSpan = (props) => <FadingSpanInternal aria-hidden={!props?.show} {...props} />;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const FadingSpanInternal = styled.span<{ show: boolean }>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginLeft: token('space.100', '8px'),
	marginRight: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.show ? 1 : 0),
	transition: 'opacity 0.5s',
	gridArea: '1 / 1',
});

const backgroundColor = cssMap({
	online: {
		backgroundColor: token('color.background.success', G50),
		transform: `scaleY(1)`,
		transition: 'none',
	},
	offline: { backgroundColor: token('color.background.accent.gray.subtlest', N20) },
});

const EditorConnectionBannerComponent = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	injectIntl(
		({
			editorType,
			triggerAnalyticsEvent,
			collabEditInternetLost,
			collabEditConnectionTimedOut = false,
			willEditorDisable = false,
			contentType,
			contentId,
			intl: { formatMessage },
		}: EditorConnectionBannerType) => {
			const [showIndicator, setShowIndicator] = useState<boolean>(false);
			const [restrictionUpdate, setRestrictionUpdate] = useState<boolean>(false);

			// initialize offlineData with defaults
			const offlineData = useRef<EditorConnectionBannerAnalyticsType>({
				browserNetworkStatus: true,
				documentVisibilityState: '',
				connectionTimeoutStartTime: 0,
				offlineStartTime: 0,
			});

			const onRestrictionUpdated = useCallback(() => {
				triggerAnalyticsEvent('offline.banner.restriction.updated', {
					editorType,
					restrictionUpdate,
				});
				return editorType === 'classic' && setRestrictionUpdate(true);
			}, [editorType, restrictionUpdate, triggerAnalyticsEvent]);

			const toggleIndicator = useCallback(
				(
					indicatorToggleCondition: boolean,
					showIndicatorCallback?: () => void,
					hideIndicatorCallback?: () => void,
				) => {
					let hideTimeout: ReturnType<typeof setTimeout> | undefined;
					let delayTimeout: ReturnType<typeof setTimeout> | undefined;
					const HIDE_FLAG_TIMEOUT = 3000;
					const DELAY_TIMEOUT = 3000;
					if (indicatorToggleCondition) {
						delayTimeout = setTimeout(() => {
							if (showIndicatorCallback) {
								showIndicatorCallback();
							}
							return setShowIndicator(true);
						}, DELAY_TIMEOUT);
					} else {
						hideTimeout = setTimeout(() => {
							if (hideIndicatorCallback) {
								hideIndicatorCallback();
							}
							//hide show indicator only when both disabling conditions are false
							//to handle edge case where both banners got set to true but we want
							// to be able to show connected banner when restored
							if (!collabEditInternetLost && !collabEditConnectionTimedOut) {
								return setShowIndicator(false);
							}
						}, HIDE_FLAG_TIMEOUT);
					}
					return { hideTimeout, delayTimeout };
				},
				[collabEditConnectionTimedOut, collabEditInternetLost],
			);

			// Upon receiving a permission change pubsub event, prepare the banner to show alternative text
			// Note: Only classic (Synchrony) editor gets offline and back online due to permission changes
			usePubSubEvent({
				contentId,
				contentType,
				eventName: `restrictionUpdated:${contentType}`,
				onEvent: onRestrictionUpdated,
			});

			useEffect(() => {
				if (!willEditorDisable) {
					// Reset restriction update boolean upon editor re-connection so next time editor goes offline it will show default messages
					setRestrictionUpdate(false);
				}
			}, [willEditorDisable]);

			useEffect(() => {
				const showIndicatorCB = () => {
					// save browser network status, start time of banner display, and document visibility state when user goes offline
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						offlineStartTime: performance.now(),
					};
					triggerAnalyticsEvent('offline.banner.show.indicator', {
						editorType,
						restrictionUpdate,
					});
				};
				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if offline banner was displayed, fire analytics
						triggerAnalyticsEvent('offline.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.offlineStartTime
								? performance.now() - offlineData?.current.offlineStartTime
								: 0,
						});
					}
				};
				const { hideTimeout, delayTimeout } = toggleIndicator(
					collabEditInternetLost,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [collabEditInternetLost]);

			useEffect(() => {
				if (collabEditInternetLost) {
					return;
				}
				const showIndicatorCB = () => {
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						connectionTimeoutStartTime: performance.now(),
					};
					triggerAnalyticsEvent('collab.edit.connection.timeout.show.indicator', {
						contentId,
						contentType,
					});
				};

				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if connection timeout banner was displayed, fire analytics
						triggerAnalyticsEvent('connection.timeout.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.connectionTimeoutStartTime
								? performance.now() - offlineData?.current.connectionTimeoutStartTime
								: 0,
						});
					}
				};

				const { hideTimeout, delayTimeout } = toggleIndicator(
					collabEditConnectionTimedOut,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [collabEditConnectionTimedOut]);

			let bannerMessage;
			if (willEditorDisable) {
				if (restrictionUpdate) {
					bannerMessage = i18n.editorDisabledDueToPermissionChange;
				} else if (collabEditConnectionTimedOut && !collabEditInternetLost) {
					bannerMessage = i18n.collabEditConnectionTimeout;
				} else {
					bannerMessage = i18n.editorDisabledOfflineMode;
				}
			} else {
				bannerMessage = i18n.editorEnabledOfflineMode;
			}

			const isOnline = () => {
				return showIndicator && !collabEditInternetLost && !collabEditConnectionTimedOut;
			};

			return (
				<StatusIndicatorOffline
					data-testid="status-indicator-offline-mode"
					show={showIndicator}
					css={[isOnline() && backgroundColor['online'], !isOnline() && backgroundColor['offline']]}
				>
					<FadingSpan
						data-testid="status-indicator-online-text"
						role="alert"
						show={!collabEditInternetLost && !collabEditConnectionTimedOut && showIndicator}
					>
						<StyledText data-testid="online-text">
							{formatMessage(i18n.editorEnabledOfflineMode)}
						</StyledText>
					</FadingSpan>
					<FadingSpan
						data-testid="status-indicator-offline-text"
						role="alert"
						show={collabEditInternetLost || collabEditConnectionTimedOut}
					>
						<StyledText data-testid="offline-text">{formatMessage(bannerMessage)}</StyledText>
					</FadingSpan>
				</StatusIndicatorOffline>
			);
		},
	),
);

export const EditorConnectionBannerOfflineMode = React.memo(EditorConnectionBannerComponent);
